import { uniqBy } from 'lodash';
import crossfilter from 'crossfilter2';

export function makeHierarchy(data) {
  const ontologies = data.map(obs => obs.ontology);
  ontologies.forEach(o => {
    if (!o.parent) {
      o.parent = null;
    }
  });

  const parents = uniqBy(
    ontologies.map(ontology => (ontology.parent ? ontology.parent : ontology)),
    'label'
  );

  return parents.map(parent => ({
    ...parent,
    children: ontologies.filter(
      ontology => ontology.parent && ontology.parent.label === parent.label
    ),
  }));
}

export function getInputVariablesFromQueries(queries, inputVariables) {
  var inputVars = [];

  Object.keys(queries).forEach(variable => {
    var query = queries[variable];
    var iv = inputVariables.find(inputVar => {
      const { label, type } = inputVar;
      if (type === 'study' || type === 'subject') {
        return label === variable;
      }
      // type is 'observation'
      const { parentLabel, is_longitudinal } = inputVar;
      const obsLabel =
        parentLabel && is_longitudinal ? `${parentLabel} - ${label}` : label;
      return obsLabel === variable;
    });
    if (iv) {
      inputVars.push({ query: query, variable: iv });
    }
  });

  return inputVars;
}

export function getCohortSubjectIds(data, c) {
  // determine which subjects are in the cohort
  const xf = crossfilter(data);
  const dimension2field = {
    left_y_axis: 'firstVisit',
    right_y_axis: 'lastVisit',
    change: 'change',
    roc: 'roc',
    value: 'value',
  };

  // identify set-valued variables
  let g2gvs = {};
  c.input_variables.forEach(sv => {
    ['subject_ontology', 'observation_ontology'].forEach(ont => {
      if (ont in sv) {
        let v_re = /^(((\S+)_|\S+_org))(\d+)$/i;
        let m1 = sv[ont].label.match(v_re);
        let m2 = sv[ont].abbreviation.match(v_re);
        let m = null;
        let afn = null;

        if (m1) {
          m = m1;
          afn = x => x[ont].label;
        } else {
          m = m2;
          afn = x => x[ont].abbreviation;
        }

        if (m && m[4] == '1') {
          let prefix = m[2];
          let v_list = [];
          c.input_variables.forEach(iv => {
            if (ont in iv && afn(iv).startsWith(prefix)) {
              v_list.push(iv[ont]);
            }
          });
          g2gvs[sv[ont].id] = v_list;
        }
      }
    });
  });

  // group queries by variable - categorical vars must be treated differently
  var var2qs = {};
  c.queries.forEach(q => {
    let v_id;
    let accessor;
    let dim;
    let dim_label = 'value';
    let gvs = null;
    let isArray = false;

    // observation variable
    if (q.input_variable.subject_ontology === undefined) {
      v_id = q.input_variable.observation_ontology.id;

      if (
        'dimension_label' in q.input_variable &&
        q.input_variable.dimension_label != ''
      ) {
        dim_label = q.input_variable.dimension_label;
      }
      const subfield = dimension2field[dim_label];
      gvs = v_id in g2gvs ? g2gvs[v_id] : [];

      // special case for grouped variable
      if (gvs.length > 1) {
        accessor = function(d) {
          return gvs.map(
            gv => d[gv.is_longitudinal ? gv.parent_id : gv.id][subfield]
          );
        };
        isArray = true;
      } else {
        accessor = function(d) {
          return d[q.input_variable.observation_ontology.id][subfield];
        };
      }
      dim = xf.dimension(accessor, isArray);
    }
    // subject variable
    else {
      v_id = q.input_variable.subject_ontology.id;
      const lbl = q.input_variable.subject_ontology.label;
      gvs = v_id in g2gvs ? g2gvs[v_id] : [];

      // special case for grouped variable
      if (gvs.length > 1) {
        accessor = function(d) {
          return gvs.map(gv => d[gv.label]);
        };
        isArray = true;
      }
      // special case for study/dataset
      else if (lbl === 'Study' || lbl === 'Dataset') {
        accessor = function(d) {
          return d.study.study_name;
        };
      } else {
        accessor = function(d) {
          return d[lbl];
        };
      }
      dim = xf.dimension(accessor, isArray);
    }

    var vkey = v_id;
    if (dim_label != null) {
      vkey += ':' + dim_label;
    }
    if (!(vkey in var2qs)) {
      var2qs[vkey] = { accessor: accessor, dim: dim, queries: [] };
    }
    var2qs[vkey]['queries'].push(q);
  });

  Object.keys(var2qs).forEach(k => {
    var v = var2qs[k];
    var filterFn = null;
    var nq = v['queries'].length;
    if (nq == 0) throw new Error('No queries for variable ' + k);

    // currently only categorical variables should have multiple queries
    if (nq > 1) {
      filterFn = function(d) {
        var any_matches = false;
        v['queries'].forEach(q => {
          if (d === q.value) {
            any_matches = true;
          }
        });
        return any_matches;
      };
    } else {
      var q = v['queries'][0];
      if (q.value !== undefined && q.value !== null) {
        filterFn = function(d) {
          return d === q.value;
        };
      } else if (q.min_value !== undefined && q.max_value !== undefined) {
        filterFn = function(d) {
          var dv = d * 1.0;
          return dv >= q.min_value && dv < q.max_value;
        };
      } else {
        throw new Error(`Unsupported query ${q}`);
      }
    }
    v['dim'].filterFunction(filterFn);
  });

  const filt = xf.allFiltered();
  const sids = {};
  filt.forEach(r => {
    sids[r.subject_id] = 1;
  });

  const cohortSubjectIds = Object.keys(sids);
  return cohortSubjectIds;
}

export function getObservationVariableAbbreviations(c) {
  var collectionVarAbbreviations = {};
  var getCollectionVarAbbreviations = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          collectionVarAbbreviations[v.abbreviation] = true;
        } else {
          getCollectionVarAbbreviations(v.children);
        }
      }
    });
  };

  getCollectionVarAbbreviations(c.observation_variables);
  return collectionVarAbbreviations;
}

export function getObservationVariableAbbreviationToName(c) {
  var collectionVarAbbreviations = {};
  var getCollectionVarAbbreviations = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          collectionVarAbbreviations[v.abbreviation] = v.label;
        } else {
          getCollectionVarAbbreviations(v.children);
        }
      }
    });
  };

  getCollectionVarAbbreviations(c.observation_variables);
  return collectionVarAbbreviations;
}

export function getObservationVariableAbbreviationToDescription(c) {
  var collectionVarAbbreviations = {};
  var getCollectionVarAbbreviations = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          collectionVarAbbreviations[v.abbreviation] = v.description;
        } else {
          getCollectionVarAbbreviations(v.children);
        }
      }
    });
  };

  getCollectionVarAbbreviations(c.observation_variables);
  return collectionVarAbbreviations;
}

export function getObservationVariableNames(c) {
  var collectionVarNames = {};
  var getCollectionVarNames = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          collectionVarNames[v.label] = true;
        } else {
          getCollectionVarNames(v.children);
        }
      }
    });
  };

  getCollectionVarNames(c.observation_variables);
  return collectionVarNames;
}

export function getObservationVariables(c) {
  var collectionVars = [];
  var collectionVarIds = {};
  var getCollectionVars = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          if (!(v.id in collectionVarIds)) {
            collectionVarIds[v.id] = true;
            collectionVars.push(v);
          }
        } else {
          getCollectionVars(v.children);
        }
      }
    });
  };

  getCollectionVars(c.observation_variables);
  return collectionVars;
}

export function getObservationVariableIds(c) {
  var collectionVarIds = {};
  var getCollectionVarIds = function(vars) {
    vars.forEach(v => {
      if (v.children && v.children.length > 0) {
        if (v.children[0].label === 'First Visit') {
          collectionVarIds[v.id] = true;
        } else {
          getCollectionVarIds(v.children);
        }
      }
    });
  };

  getCollectionVarIds(c.observation_variables);
  return Object.keys(collectionVarIds);
}

export function getCollectionVisitCounts(c, which) {
  var counts = [];
  var ch = {};
  var obs_vars = c.observation_variables_list
    ? c.observation_variables_list
    : c.observation_variables;

  obs_vars.forEach(ov => {
    var evt = null;
    if (ov[which + '_visit_event'] != null) {
      evt = ov[which + '_visit_event'];
    } else if (ov[which + '_visit_num'] != null) {
      evt = ov[which + '_visit_num'];
    }

    if (!(evt in ch)) {
      ch[evt] = 0;
    }
    ch[evt] += 1;
  });

  // sort by decreasing count
  Object.keys(ch)
    .sort((a, b) => ch[b] - ch[a])
    .forEach(k => {
      counts.push({ visit: k, count: ch[k] });
    });

  return counts;
}

export function getCollectionDescription(c) {
  var descr = c.label + ': ';
  const has_obs_vars =
    'observation_variables_list' in c || 'observation_variables' in c;
  const has_subj_vars = 'subject_variables' in c;

  var nc = null;
  if (c.num_cohorts) {
    nc = c.num_cohorts;
    descr += ' ' + nc + (nc == 1 ? 'cohort.' : ' cohorts.');
  } else if (c.cohorts) {
    nc = c.cohorts.length;
    descr += ' ' + nc + (nc == 1 ? 'cohort.' : ' cohorts.');
  }

  if (has_subj_vars && has_obs_vars) {
    var obs_vars = c.observation_variables_list
      ? c.observation_variables_list
      : c.observation_variables;
    var nsv = c.subject_variables.length;
    var nov = obs_vars.length;
    descr += ' ' + (nsv + nov) + ' variables ';
  }

  if ('studies' in c) {
    var nd = c.studies.length;
    descr +=
      'from ' + nd + ' uploaded ' + (nd == 1 ? 'dataset' : 'datasets') + ' [';
    descr += c.studies.map(s => s.study.study_name).join(',');
    descr += ']';
  }

  if (c.has_visits_set) {
    var fvs = getCollectionVisitCounts(c, 'first');
    var lvs = getCollectionVisitCounts(c, 'last');
    descr +=
      ' First Visits: ' + fvs.map(v => v.visit + '[' + v.count + ']').join(',');
    descr +=
      ' Last Visits: ' + lvs.map(v => v.visit + '[' + v.count + ']').join(',');
  }
  return descr;
}

// extract metadata for a single observation variable
export function getVariablePureToneAudiometryMetadata(ov) {
  let ear = null;
  let conduction = null;
  let freq_hz = null;
  let vname = 'ontology' in ov ? ov.ontology.abbreviation : ov.abbreviation;

  // AudBase
  let m = vname.match(/^PureTone_([AB])U([LRAUCI])_(\d+)$/);
  if (m) {
    conduction = m[1] == 'A' ? 'air' : 'bone';
    ear = m[2];
    freq_hz = m[3] * 1.0;
  } else {
    // NHANES
    m = vname.match(/^(A)UXU(\d+)(K)?([12])?([LRAUCI])$/);
    if (m) {
      if (m[4] != '2') {
        conduction = m[1] == 'A' ? 'air' : 'bone';
        ear = m[5];
        freq_hz = m[3] == 'K' ? m[2] * 1000.0 : m[2] * 1.0;
      }
    } else {
      // NIDCD
      m = vname.match(/^([LRAUCI])E_([AB])C_(\d+)$/);
      if (m) {
        ear = m[1];
        conduction = m[2] == 'A' ? 'air' : 'bone';
        freq_hz = m[3];
      } else {
        // Stanford
        m = vname.match(/^([LRAUCI])E_(\d+)_(air|bone)$/);
        if (m) {
          ear = m[1];
          freq_hz = m[2];
          conduction = m[3];
        } else {
          // pure tone average
          m = vname.match(/^(left|right)_(\d|hf)pta_(air|bone)$/i);
          if (m) {
            ear = m[1];
            freq_hz = m[2];
            conduction = m[3];
          }
        }
      }
    }
  }
  return { ear: ear, conduction: conduction, freq_hz: freq_hz };
}

export function getVariableHasPureToneAudiometryData(v) {
  if (v == null) return false;
  let md = getVariablePureToneAudiometryMetadata(v);
  return md.ear != null;
}

// whether a collection has pure tone audiometry data that can be displayed in the Audiogram component
export function getCollectionHasPureToneAudiometryData(c) {
  if ('has_pure_tone_audiometry_data' in c) {
    return c['has_pure_tone_audiometry_data'];
  }
  let ovars = c.observation_variables_list;
  let hasAudiometryData = false;
  if (ovars == null) ovars = [];
  ovars.forEach(ov => {
    if (
      // AudBase
      ov.ontology.abbreviation.match(/^PureTone_([AB])U([RLAUCI])_(\d+)$/) ||
      // NHANES
      ov.ontology.abbreviation.match(/^(AU)XU(\d+)(K)?([12])?([LRAUCI])$/) ||
      // NIDCD
      ov.ontology.abbreviation.match(/^(RE|LE|AE|UE|CE|IE)_(AC|BC)_\d+$/) ||
      // Stanford
      ov.ontology.abbreviation.match(/^(RE|LE|AE|UE|CE|IE)_(\d+)_(air|bone)$/)
    ) {
      hasAudiometryData = true;
    }
  });
  return hasAudiometryData;
}

// return min/max pure tone frequency for a collection with pure tone audiometry data
export function getCollectionPureToneAudiometryFrequencyRange(c) {
  if (c == null || !('observation_variables_list' in c))
    return { min: 250, max: 8000 };

  let ovars = c.observation_variables_list;
  let minFreq = null;
  let maxFreq = null;
  ovars.forEach(ov => {
    let freq = null;

    // AudBase
    let m = ov.ontology.abbreviation.match(
      /^PureTone_([AB])U([LRAUCI])_(\d+)$/
    );
    if (m) {
      freq = m[3];
    }
    // NHANES
    m = ov.ontology.abbreviation.match(/^(AU)XU(\d+)(K)?([12])?([LRAUCI])$/);
    if (m) {
      freq = m[2];
      let k = m[3];
      if (k == 'K') freq *= 1000;
    }
    // NIDCD
    m = ov.ontology.abbreviation.match(/^(RE|LE|AE|UE|CE|IE)_(AC|BC)_(\d+)$/);
    if (m) {
      freq = m[3];
    }
    // Stanford
    m = ov.ontology.abbreviation.match(
      /^(RE|LE|AE|UE|CE|IE)_(\d+)_(air|bone)$/
    );
    if (m) {
      freq = m[2];
    }

    if (freq != null) {
      if (minFreq == null || freq < minFreq) minFreq = freq;
      if (maxFreq == null || freq > maxFreq) maxFreq = freq;
    }
  });
  return { min: minFreq, max: maxFreq };
}

// ear_list - list of one or more of 'L', 'R', 'A', 'U', 'C', 'I'
//  L = left
//  R = right
//  A = affected
//  U = unaffected
//  C = contralateral
//  I = ipsilateral
export function getCollectionHasAudiometryDataByEars(c, ear_list) {
  if (c == null || !('observation_variables_list' in c)) return false;
  let ovars = c.observation_variables_list;
  let ears = {};
  ovars.forEach(ov => {
    // AudBase
    let m = ov.ontology.abbreviation.match(/^PureTone_[AB]U([LRAUCI])_\d+$/);
    if (m) {
      ears[m[1]] = true;
    }
    // NHANES
    m = ov.ontology.abbreviation.match(/^AUXU\d+K?[12]?([LRAUCI])$/);
    if (m) {
      ears[m[1]] = true;
    }
    m = ov.ontology.abbreviation.match(/^([LRAUCI])E_(AC|BC)_\d+$/);
    if (m) {
      ears[m[1]] = true;
    }
    // Stanford
    m = ov.ontology.abbreviation.match(/^([LRAUCI])E_(\d+)_(air|bone)$/);
    if (m) {
      ears[m[1]] = true;
    }
  });
  let ear_present = false;
  ear_list.forEach(e => {
    if (e in ears) {
      ear_present = true;
    }
  });
  return ear_present;
}

export function getCollectionHasAffectedEarAudiometryData(c) {
  return getCollectionHasAudiometryDataByEars(c, ['A', 'U']);
}

export function getCollectionHasContralateralIpsilateralEarAudiometryData(c) {
  return getCollectionHasAudiometryDataByEars(c, ['C', 'I']);
}

// Remap negatively-encoded AudBase No Response values.
export function remapAudBasePureToneNoResponseValues(collection, data) {
  // identify PureTone variables
  let ptVars = {};
  collection.observation_variables_list.forEach(ov => {
    if (
      ov.ontology.abbreviation.match(/^PureTone_\S+_(\d+)|PureToneAvg\S+_dB$/)
    ) {
      ptVars[ov.ontology.id] = 1;
    }
  });

  // if No Response at 70dB then remap to 70 + ADD_DB
  const ADD_DB = 5;

  // [-180, -220] dB -> No Response at [-20, 20] dB
  // -X dB = No Response at X dB
  let remapDecibels = function(db) {
    let new_db = db;
    if (db >= -220 && db <= -180) {
      new_db = Math.abs(db) - 200 + ADD_DB;
    } else if (db < -20) {
      new_db = Math.abs(db) + ADD_DB;
    }
    return new_db;
  };

  // remap data
  data.forEach(d => {
    if (d.observation in ptVars) {
      if (d.is_longitudinal) {
        // TODO - remap first visit, last visit, and change, but may need additional info for rate of change
      } else {
        d.value = remapDecibels(d.value);
      }
    }
  });

  // TODO - do the same with data.raw_data? may only be an issue for longitudinal case
}

// PPMI-specific workaround
export function sortByVisitEvent(unsorted_list, event_accessor_fn) {
  var numericEvents = true;
  var pseudoNumericEvents = true;
  var baselineRE = /^baseline$/i;
  var pnRE = /^[A-Z_]+(-?[\d.]+)$/i;

  unsorted_list.forEach(e => {
    var evt = event_accessor_fn(e);
    if (typeof evt == 'string' && !evt.match(pnRE) && !evt.match(baselineRE)) {
      pseudoNumericEvents = false;
    }
    if (isNaN(evt)) {
      numericEvents = false;
    }
  });

  // sort numerically
  if (numericEvents) {
    return unsorted_list.sort(
      (a, b) => event_accessor_fn(a) - event_accessor_fn(b)
    );
  }
  // sort numerically, ignorning non-numeric component and setting "BASELINE" to 0
  if (pseudoNumericEvents) {
    var pnAccFn = a => {
      var evt = event_accessor_fn(a);
      if (evt.match(baselineRE)) return 0;
      var m = evt.match(pnRE)[1];
      return m;
    };

    return unsorted_list.sort((a, b) => pnAccFn(a) - pnAccFn(b));
  }

  // sort alphabetically but put PPMI and EMA events in the correct order
  var uniqueEvents = [
    'Pre',
    '3 mo',
    '6 mo',
    'SC',
    'baseline',
    'Baseline',
    'BASELINE',
    'BL',
    'V1',
    'V2',
    'V3',
    'V4',
    'V5',
    'V6',
    'V7',
    'V8',
    'V9',
    'V01',
    'V02',
    'V03',
    'V04',
    'V05',
    'V06',
    'V07',
    'V08',
    'V09',
    'V10',
    'V11',
    'V12',
    'V13',
    'V14',
    'V15',
    'V16',
    'V17',
    'V18',
    'V19',
    'V20',

    '1_months',
    '2_months',
    '3_months',
    '3_mos',
    '4_months',
    '5_months',
    '6_months',
    '6_mos',
    '7_months',
    '8_months',
    '9_months',
    '9_mos',
    '10_months',
    '11_months',
    '12_months',
    '12_mos',
    '13_months',
    '14_months',
    '15_months',
    '16_months',
    '17_months',
    '18_months',
    '18_mos',
    '19_months',
    '20_months',
    '21_months',
    '22_months',
    '23_months',
    '24_months',
    '24_mos',
    '30_mos',
    '36_mos',
    '3_years',
    '42_mos',
    '48_mos',
    '4_years',
    '54_mos',
    '60_mos',
    '5_years',
    '66_mos',
    '72_mos',
    '6_years',
    '78_mos',
    '84_mos',
    '7_years',
    '90_mos',
    '96_mos',
    '8_years',
    '102_mos',
    '108_mos',
    '9_years',
    '114_mos',
    '120_mos',
    '10_years',
    '126_mos',
    '132_mos',
    '11_years',
    '138_mos',
    '144_mos',
    '150_mos',
    '156_mos',
    '16_years',
    '21_years',
    '26_years',
    '31_years',
    '36_years',
    '41_years',

    'PW',
    'ST',
    'U01',

    // Stanford audiometry
    'DAY_10',
    'DAY_20',
    'DAY_30',
    'DAY_40',
    'DAY_50',
    'DAY_60',
    'DAY_70',
    'DAY_80',
    'DAY_90',
    'DAY_100',
    'DAY_200',
    'DAY_300',
    'DAY_400',
    'DAY_500',
    'DAY_750',
    'DAY_1000',
    'DAY_1500',

    // Stanford audiometry v2

    'PERI_TREATMENT',
    'POST_TREATMENT',
    'FOLLOW_UP',
    'FOLLOW_UP_2',
    'FOLLOW_UP_3',
    'FOLLOW_UP_4',
    'FOLLOW_UP_5',
    'FOLLOW_UP_6',
    'FOLLOW_UP_7',
    'FOLLOW_UP_8',
    'FOLLOW_UP_9',

    // Stanford audiometry v3
    '0_cisplatin_mgm2',
    '50_cisplatin_mgm2',
    '100_cisplatin_mgm2',
    '150_cisplatin_mgm2',
    '200_cisplatin_mgm2',
    '250_cisplatin_mgm2',
    '300_cisplatin_mgm2',
    '350_cisplatin_mgm2',
    '400_cisplatin_mgm2',
    '450_cisplatin_mgm2',
    '500_cisplatin_mgm2',
    '550_cisplatin_mgm2',
    '600_cisplatin_mgm2',
    '1000_cisplatin_mgm2',
    '0_days',
    '200_days',
    '400_days',
    '600_days',
    '800_days',
    '1000_days',
  ];

  var evtIdx = {};
  var ind = 1;
  uniqueEvents.forEach(e => {
    evtIdx[e] = ind++;
  });
  function getIndex(evt) {
    return evt in evtIdx ? evtIdx[evt] : ind + 1;
  }

  function sortFn(a, b) {
    var a_evt = event_accessor_fn(a);
    var b_evt = event_accessor_fn(b);
    var a_i = getIndex(a_evt);
    var b_i = getIndex(b_evt);
    if (a_i < b_i) return -1;
    if (b_i < a_i) return 1;
    return a_evt.localeCompare(b_evt);
  }

  return unsorted_list.sort(sortFn);
}

export function sortVisitEvents(events) {
  var acc_fn = x => x;
  let sorted = sortByVisitEvent(events, acc_fn);

  // PPMI-specific rewrite/filter
  let ppmi_filter_re = /^(ST|U\d\d|PW)$/;
  var new_events = sorted.filter(e => !e.toString().match(ppmi_filter_re));
  return new_events;
}

export function strCompare(s1, s2) {
  if (s1 < s2) return -1;
  if (s2 < s1) return 1;
  return 0;
}

// Comparison function for audiometric (and other) variables with a non-numeric prefix
// and numeric suffix.
export function numericSuffixStrCompare(s1, s2) {
  let null1 = typeof s1 === 'undefined' || s1 === null;
  let null2 = typeof s2 === 'undefined' || s2 === null;

  if (null1 && !null2) return -1;
  if (null2 && !null1) return 1;
  if (null1 && null2) return 0;

  let str1 = null;
  let str2 = null;
  let num1 = null;
  let num2 = null;

  // non-numeric prefix + numeric suffix
  const var_re = /^([^\d]*)(\d+)$/;
  let m1 = s1.toLowerCase().match(var_re);
  let m2 = s2.toLowerCase().match(var_re);

  if (m1 && m2) {
    str1 = m1[1];
    str2 = m2[1];
    num1 = m1[2] * 1.0;
    num2 = m2[2] * 1.0;
  } else {
    // special case 1 - audiometric frequency value in the middle e.g., LE_2000_air
    const mid_re = /^(RE|LE|AE|UE|CE|IE)_(\d+)_(air|bone)$/i;
    let mm1 = s1.toLowerCase().match(mid_re);
    let mm2 = s2.toLowerCase().match(mid_re);

    if (mm1 && mm2) {
      str1 = mm1[1] + mm1[3];
      num1 = mm1[2] * 1.0;
      str2 = mm2[1] + mm2[3];
      num2 = mm2[2] * 1.0;
    }
    // special case 2 - encoded frequency value in the middle e.g., AUXU500R, AUXU4KR, AUXU1K1R, AUXU1K2R
    else {
      const nhanes_re = /^(AUXU)(\d+)(K?)([12]?[LRAUCI])$/i;
      let nh1 = s1.toLowerCase().match(nhanes_re);
      let nh2 = s2.toLowerCase().match(nhanes_re);

      if (nh1 && nh2) {
        str1 = nh1[1] + nh1[4];
        num1 = nh1[2] * (nh1[3] == 'K' ? 1000.0 : 1.0);
        str2 = nh2[1] + nh2[4];
        num2 = nh2[2] * (nh2[3] == 'K' ? 1000.0 : 1.0);
      }
      // special case 3 - "Right threshold @ 8000Hz (db)"
      else {
        const nhanes_re2 = /^(.*)@ (\d+)Hz(.*)$/i;
        let nh1 = s1.toLowerCase().match(nhanes_re2);
        let nh2 = s2.toLowerCase().match(nhanes_re2);

        if (nh1 && nh2) {
          str1 = nh1[1];
          num1 = nh1[2] * 1.0;
          str2 = nh2[1];
          num2 = nh2[2] * 1.0;
        }
        // special case 4 - "PT HL dB air left 1500Hz"
        else {
          const pt_re1 = /^(PT HL dB.*) (\d+)Hz$/i;
          let nh1 = s1.toLowerCase().match(pt_re1);
          let nh2 = s2.toLowerCase().match(pt_re1);

          if (nh1 && nh2) {
            str1 = nh1[1];
            num1 = nh1[2] * 1.0;
            str2 = nh2[1];
            num2 = nh2[2] * 1.0;
          }
        }
      }
    }
  }

  // compare string component first then numeric component
  if (str1 != null && str2 != null) {
    // compare prefixes first
    //    let pc = strCompare(str1, str2);
    let pc = str1.localeCompare(str2);
    if (pc != 0) return pc;
    // compare numeric component
    if (num1 < num2) return -1;
    if (num2 < num1) return 1;
    return 0;
  }
  // regular string comparison
  else {
    //    return strCompare(s1.toLowerCase(), s2.toLowerCase());
    return s1.toLowerCase().localeCompare(s2.toLowerCase());
  }
}

// Return all groups of related variables in a collection..
export function getVariableGroups(vars) {
  let pref2vars = {};
  // group variables by prefix
  vars.forEach(v => {
    let prefix = null;
    let index = null;

    // variables of the form something_1, something_2, etc.
    const v_re = /^((\S+)_(org)?)(\d+)$/i;
    let m1 = v.scale.match(v_re);
    let m2 = v.abbreviation.match(v_re);
    if (m1) {
      prefix = m1[1];
      index = m1[4];
    } else if (m2) {
      prefix = m2[1];
      index = m2[4];
    }
    // special cases for audiometric vars
    else {
      const mid_re = /^(RE|LE|AE|UE|CE|IE)_(\d+)_(air|bone)$/i;
      let m = v.scale.match(mid_re);
      if (m) {
        prefix = m[1] + m[3];
        index = m[2] * 1.0;
      } else {
        const nhanes_re = /^(AUXU)(\d+)(K?)([12]?[LRAUCI])$/i;
        m = v.scale.match(nhanes_re);
        if (m) {
          prefix = m[1] + m[4] + m[5];
          index = m[2] * (m[3] == 'K' ? 1000.0 : 1.0);
        } else {
          const nhanes_re2 = /^(.*)@ (\d+)Hz(.*)$/i;
          m = v.scale.match(nhanes_re2);
          if (m) {
            prefix = m[1];
            index = m[2] * 1.0;
          }
        }
      }
    }
    if (prefix != null) {
      if (!(prefix in pref2vars)) {
        pref2vars[prefix] = [];
      }
      pref2vars[prefix].push({ var: v, prefix: prefix, index: index * 1.0 });
    }
  });

  // further subdivide groups into 1-100 / <= 8000 / > 8000
  let groups = [];
  Object.keys(pref2vars).forEach(k => {
    let svars = pref2vars[k].sort((a, b) => {
      return a.index - b.index;
    });
    let last_index = null;
    let group = null;

    svars.forEach(v => {
      let newGroup = false;
      // new group
      if (last_index == null) {
        newGroup = true;
      } else {
        let diff = v.index - last_index;
        if (v.index <= 100 && diff > 1) {
          newGroup = true;
        } else if (v.index > 200 && last_index <= 100) {
          newGroup = true;
        } else if (v.index > 8000 && last_index <= 8000) {
          newGroup = true;
        }
      }
      if (newGroup) {
        group = { id: groups.length + 1, vars: [v], min_index: v.index };
        groups.push(group);
      } else {
        group['vars'].push(v);
      }
      last_index = v.index;
    });
  });

  let v2g = {};
  groups.forEach(g => {
    g.vars.map(v => {
      v2g[v['var'].scale] = g;
    });
  });
  return { groups: groups, var2group: v2g };
}

export function getSetValuedVariableGroups(vars) {
  // group variables by prefix
  let pref2vars = {};

  // only categorical variables can be set-valued
  vars
    .filter(v => v['data_category'] == 'Categorical')
    .forEach(v => {
      let prefix = null;
      let index = null;
      let field = null;

      // variables of the form something_1, something_2, etc.
      const v_re = /^((\S+)_(org)?)(\d+)$/i;
      let m1 = v.label.match(v_re);
      let m2 = v.abbreviation.match(v_re);

      if (m1) {
        prefix = m1[1];
        index = m1[4];
        field = 'label';
      } else if (m2) {
        prefix = m2[1];
        index = m2[4];
        field = 'abbreviation';
      }
      if (prefix != null) {
        if (!(prefix in pref2vars)) {
          pref2vars[prefix] = [];
        }
        pref2vars[prefix].push({
          var: v,
          prefix: prefix,
          index: index * 1.0,
          field: field,
        });
      }
    });

  // filter groups - index must start at 1 and all vars must have the same field
  let groups = [];
  Object.keys(pref2vars).forEach(k => {
    let svars = pref2vars[k].sort((a, b) => {
      return a.index - b.index;
    });
    let group_is_ok = true;
    if (svars[0].index != 1 && !svars[0].prefix.match(/^AcReflexDcy_Stim.*/)) {
      group_is_ok = false;
      //      console.log(
      //        'group with prefix=' +
      //          svars[0].prefix +
      //          ' not valid; starts at ' +
      //          svars[0].index
      //      );
    }
    let fields_h = {};
    svars.forEach(sv => {
      fields_h[sv['field']] = 1;
    });
    const fields = Object.keys(fields_h);
    if (fields.length != 1) {
      group_is_ok = false;
      //      console.log(
      //        'group with prefix=' +
      //          svars[0].prefix +
      //          ' not valid; num fields = ' +
      //          fields.length
      //      );
    }

    if (group_is_ok)
      groups.push({ prefix: svars[0].prefix, field: fields[0], vars: svars });
  });

  let v2g = {};
  let v2v = {};
  groups.forEach(g => {
    g.vars.map(v => {
      v2g[v['var'].label] = g;
      v2v[v['var'].label] = v;
    });
  });
  return { groups: groups, var2group: v2g, var2var: v2v };
}

export function scaleSortFn(sa, sb) {
  var a = sa.ontology ? sa.ontology : sa;
  var b = sb.ontology ? sb.ontology : sb;
  // subject variables before observation, then sorted by group and scale name
  if (a.type == 'subject' && b.type == 'observation') return -1;
  if (a.type == 'observation' && b.type == 'subject') return 1;
  let gc = numericSuffixStrCompare(a.group, b.group);
  if (gc != 0) return gc;
  let lc = numericSuffixStrCompare(a.label, b.label);
  if (lc != 0) return lc;
  return numericSuffixStrCompare(a.scale, b.scale);
}

export function assignScaleGroups(scales) {
  scales.forEach(s => {
    // UMD HOME
    if (s.abbreviation.match(/^CIRS-G /)) {
      s.group = 'Cumulative Illness Rating Scale - Geriatric Version (CIRS-G)';
    } else if (s.abbreviation.match(/^BSI-18 /)) {
      s.group = 'Brief Symptom Inventory 18';
    } else if (s.abbreviation.match(/^(Profile29|PROMIS-29-Profile)/)) {
      s.group =
        'Patient-Reported Outcomes Measurement Information System PROMIS-29';
    } else if (s.abbreviation.match(/^PROMIS_GlobalHealth/)) {
      s.group =
        'Patient-Reported Outcomes Measurement Information System (PROMIS) Global Health Questionnaire';
    } else if (s.abbreviation.match(/^OARS /)) {
      s.group = 'Older Americans Resources and Services (OARS) Scales';
    } else if (s.abbreviation.match(/^UPDRS (Part|Total) /)) {
      s.group =
        "Unified Parkinson's Disease Rating Scale (UPDRS) Summary Scores";
    } else if (s.abbreviation.match(/^UPDRS_(01|02|03|04)/)) {
      s.group =
        "Unified Parkinson's Disease Rating Scale (UPDRS) Part I - Mentation, Behavior, and Mood";
    } else if (
      s.abbreviation.match(/^UPDRS_(05|06|07|08|09|10|11|12|13|14|15|16|17)/)
    ) {
      s.group =
        "Unified Parkinson's Disease Rating Scale (UPDRS) Part II - Activities of Daily Living";
    } else if (
      s.abbreviation.match(/^UPDRS_(18|19|20|21|22|23|24|25|26|27|28|29|30|31)/)
    ) {
      s.group =
        "Unified Parkinson's Disease Rating Scale (UPDRS) Part III - Motor Exam";
    } else if (
      s.abbreviation.match(/^UPDRS_(32|33|34|35|36|37|38|39|40|41|42)/)
    ) {
      s.group =
        "Unified Parkinson's Disease Rating Scale (UPDRS) Part IV - Complications of Therapy";
    }
    // UMD HOME Improvement
    else if (s.abbreviation.match(/^(CIRS-|cirs[tcs])/i)) {
      s.group = 'Cumulative Illness Rating Scale (CIRS)';
    } else if (s.abbreviation.match(/^ASTDEV-/)) {
      s.group = 'Assistive Devices';
    } else if (s.abbreviation.match(/^P[PR]29-/)) {
      s.group =
        'Patient-Reported Outcomes Measurement Information System PROMIS-29 v2';
    } else if (s.abbreviation.match(/^PROMIS ACOG/)) {
      ('Patient-Reported Outcomes Measurement Information System (PROMIS) Applied Cognition');
    } else if (s.abbreviation.match(/^PROMIS-GH/)) {
      ('Patient-Reported Outcomes Measurement Information System (PROMIS) Global Health Questionnaire');
    } else if (s.abbreviation.match(/^SCOPA-AUT/)) {
      s.group =
        "Scales for Outcomes in Parkinson's disease-Autonomic (SCOPA-AUT)";
    } else if (s.abbreviation.match(/^(PSS-STRESS|Perceived_Stress)/)) {
      s.group = 'Perceived Stress Scale';
    } else if (s.abbreviation.match(/^OARS\d+-/)) {
      s.group = 'Older Americans Resources and Services (OARS) Scales';
    } else if (s.abbreviation.match(/^AD_/)) {
      s.group = 'Ascertain Dementia - AD8';
    } else if (s.abbreviation.match(/^Moca/i)) {
      s.group = 'Montreal Cognitive Assessment (MoCA)';
    }
    // default - group is set to category/domain (e.g., Demographics, Cognitive, Motor)
    else {
      s.group = s.category;
    }
  });
}

export function sortScales(scales) {
  assignScaleGroups(scales);
  return scales.sort(scaleSortFn);
}

export function getLongScaleNameDefault(datasets) {
  const long_re = / EMA /;
  var useLong = false;
  datasets.forEach(sd => {
    if (sd.study_name.match(long_re)) {
      useLong = true;
    }
  });
  return useLong;
}

export function getLabelWidth(label) {
  const fm = 17;
  if (label.length <= 3) {
    return label.length * fm * 2.2;
  } else if (label.length <= 6) {
    return label.length * fm * 1.4;
  } else if (label.length >= 15) {
    return label.length * fm * 0.8;
  }
  return label.length * fm;
}

/**
 * Given a list of [variable_id, first_index, last_index], generate a set of subject counts
 * using the data in subject_variable_visits.
 *
 * subject_variable_visits - value returned by /api/studies/subject_variable_visits
 * which - either 'event' or 'num'
 */
export function countSubjectsByVisits(subject_variable_visits, vars, which) {
  var subjCounts = { all: 0 };
  var subjGrouped = { all: [] };
  var subjs = subject_variable_visits['subjects'];
  var subjIds = Object.keys(subjs);

  // subjs:
  // "subjects": {
  //   <subj_id>: {
  //    "25": 1,     <- subject_ontology
  //    "26": 1,
  //    "361": {     <- observation_ontology
  //  	"event": "100000000000000000000",
  //           "num": "000000000010000000"
  //            },
  //   }
  // }

  subjIds.forEach(sid => {
    var s = subjs[sid];
    var include_subj = true;
    var study_ids = Object.keys(s);
    study_ids.forEach(study_id => {
      if (!(study_id in subjCounts)) {
        subjCounts[study_id] = 0;
        subjGrouped[study_id] = [];
      }
      vars.forEach(v => {
        let vtype = v[0];
        let vid = v[1];
        if (!(vid in s[study_id][vtype])) {
          include_subj = false;
        } else if (typeof s[study_id][vtype][vid] != 'number') {
          var vstring = s[study_id][vtype][vid][which];
          if (vstring.charAt(v[2]) == '0' || vstring.charAt(v[3]) == '0') {
            include_subj = false;
          }
        }
      });
    });
    if (vars.length > 0 && include_subj) {
      subjCounts['all'] += 1;
      subjGrouped['all'].push(parseInt(sid));
      study_ids.forEach(study_id => {
        subjCounts[study_id] += 1;
        subjGrouped[study_id].push(parseInt(sid));
      });
    }
  });
  return { counts: subjCounts, subjects: subjGrouped };
}

/**
 * Count the maximum number of subjects with at least two visits for the specified
 * variable, returinng the first and last visit selections that produce the highest
 * count (i.e., the first and last visit that maximize the number of subjects with
 * a measurement for the specified variable.)
 *
 * subject_variable_visits - value returned by /api/studies/subject_variable_visits
 * svar - the variable to test
 * which - either 'event' or 'num'
 */
export function countMaxSubjects(subject_variable_visits, svar, which) {
  var subjs = subject_variable_visits['subjects'];
  var subjIds = Object.keys(subjs);
  var visits = subject_variable_visits['visits'][which];
  var n_visits = visits.length;

  const vid = svar.id;
  const vtype = svar.type;

  // subjects for each study and first visit-last visit pair
  var subjects = {};

  // special handling for subject variables (no visits)
  var subjectVarSubjects = { all: [] };

  subjIds.forEach(sid => {
    var s = subjs[sid];
    var study_ids = Object.keys(s);
    study_ids.forEach(study_id => {
      if (vid in s[study_id][vtype]) {
        // subject variable, no per-visit info
        if (typeof s[study_id][vtype][vid] == 'number') {
          if (!(study_id in subjectVarSubjects)) {
            subjectVarSubjects[study_id] = [];
          }
          subjectVarSubjects['all'].push(sid);
          subjectVarSubjects[study_id].push(sid);
        }
        // observation variable
        else {
          var vstring = s[study_id][vtype][vid][which];
          // cross-sectional - single visit
          if (n_visits == 1) {
            const key = '0:0';
            if (!(key in subjects)) {
              subjects[key] = { all: [] };
            }
            if (!(study_id in subjects[key])) {
              subjects[key][study_id] = [];
            }
            if (vstring == '1') {
              subjects[key]['all'].push(sid);
              subjects[key][study_id].push(sid);
            }
          }
          // longitudinal - check all pairs of visits
          else {
            for (var vis1 = 0; vis1 < n_visits; ++vis1) {
              for (var vis2 = vis1 + 1; vis2 < n_visits; ++vis2) {
                const key = vis1 + ':' + vis2;
                if (!(key in subjects)) {
                  subjects[key] = { all: [] };
                }
                if (!(study_id in subjects[key])) {
                  subjects[key][study_id] = [];
                }
                if (
                  vstring.charAt(vis1) == '1' &&
                  vstring.charAt(vis2) == '1'
                ) {
                  subjects[key]['all'].push(sid);
                  subjects[key][study_id].push(sid);
                }
              }
            }
          }
        }
      }
    });
  });

  // subject variable case
  const svs_len = subjectVarSubjects['all'].length;
  if (svs_len > 0) {
    let cts = {};
    let sbs = {};
    Object.keys(subjectVarSubjects).forEach(k => {
      sbs[k] = subjectVarSubjects[k];
      cts[k] = sbs[k].length;
    });
    return {
      counts: cts,
      subjects: sbs,
      visits: [['subject', vid, 0, 0]],
    };
  }

  // sort by descending count
  var counts = [];
  Object.keys(subjects).forEach(k => {
    let vs = k.split(':');
    const fv = Number(vs[0]);
    const lv = Number(vs[1]);
    let subjs = subjects[k];
    let subjCounts = {};
    Object.keys(subjs).forEach(group => {
      subjCounts[group] = subjs[group].length;
    });
    counts.push({
      first: fv,
      last: lv,
      counts: subjCounts,
      subjects: subjs,
      count: subjs['all'].length,
    });
  });

  counts.sort((a, b) => b['count'] - a['count']);
  const max_count = counts[0];
  const max_vis = [[vtype, vid, max_count['first'], max_count['last']]];
  return {
    counts: max_count['counts'],
    subjects: max_count['subjects'],
    visits: max_vis,
  };
}

/**
 * Estimate the maximum number of subjects that could be obtained with the selected
 * set of variables, returning the first and last visit selections that would produce
 * the estimated result.
 *
 * subject_variable_visits - value returned by /api/studies/subject_variable_visits
 * svars - list of variables
 * which - either 'event' or 'num'
 */
export function estimateMaxSubjects(subject_variable_visits, svars, which) {
  // simple heuristic based on selecting the top-scoring first and last visit for each variable
  var vars = [];
  svars.forEach(svar => {
    let ms = countMaxSubjects(subject_variable_visits, svar, which);
    vars.push(ms['visits'][0]);
  });

  var { counts, subjects } = countSubjectsByVisits(
    subject_variable_visits,
    vars,
    which
  );
  // TODO - try additional combinations (e.g., with 2nd or 3rd best option) if this yields zero

  return { counts: counts, subjects: subjects, visits: vars };
}

/**
 * Variant of estimateMaxSubjects that tries to maximize the time between first
 * and last visit.
 *
 * subject_variable_visits - value returned by /api/studies/subject_variable_visits
 * var_ids - list of observation variable ids
 * which - either 'event' or 'num'
 * min_subjects - minimum number of subjects to return
 */
export function estimateMaxVisits(
  subject_variable_visits,
  svars,
  which,
  min_subjects
) {
  var subjs = subject_variable_visits['subjects'];
  var subjIds = Object.keys(subjs);
  var visits = subject_variable_visits['visits'][which];
  var n_visits = visits.length;

  var vars = [];

  svars.forEach(svar => {
    let vid = svar.id;
    let vtype = svar.type;
    // get visit counts for variable vid
    var visitCounts = [];

    subjIds.forEach(sid => {
      var s = subjs[sid];
      var study_ids = Object.keys(s);
      study_ids.forEach(study_id => {
        if (
          vid in s[study_id][vtype] &&
          typeof s[study_id][vtype][vid] != 'number'
        ) {
          var vstring = s[study_id][vtype][vid][which];
          for (var vis = 0; vis < n_visits; ++vis) {
            if (!(vis in visitCounts))
              visitCounts[vis] = { index: vis, count: 0 };
            if (vstring.charAt(vis) == '1') {
              visitCounts[vis]['count'] += 1;
            }
          }
        }
      });
    });

    visitCounts.sort((a, b) => b['count'] - a['count']);
    var first_index = 0;
    var last_index = 0;

    // make the first visit the one with the most subjects
    if (visitCounts.length > 1) {
      if (visitCounts[0]['index'] < visitCounts[1]['index']) {
        first_index = visitCounts[0]['index'];
      } else {
        first_index = visitCounts[1]['index'];
      }
      last_index = n_visits - 1;
    }
    vars.push([vtype, vid, first_index, last_index]);
  });

  // move last index backwards until we exceed the desired threshold or
  // hit the first index in at least one variable
  let done = false;
  var counts = null;

  while (!done) {
    counts = countSubjectsByVisits(subject_variable_visits, vars, which);
    // halt if desired threshold exceeded
    if (counts['counts']['all'] >= min_subjects) {
      done = true;
      break;
    }

    let last_index = vars[0][3];
    let new_vars = [];
    vars.forEach(v => {
      let nv = [v[0], v[1], v[2], last_index - 1];
      new_vars.push(nv);

      // halt when first + last coincide
      if (nv[3] == nv[2]) {
        done = true;
      }
    });
    if (!done) vars = new_vars;
  }
  counts = countSubjectsByVisits(subject_variable_visits, vars, which);
  return { counts: counts, visits: vars };
}

export function hexToRGB(hex) {
  // if Hex Is RGB or RBGA dont convert return original
  const RGBA = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d*(?:\.\d+)?)\)$/;
  let RGB = /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/;
  const boolRGBA = RGBA.test(hex);
  const boolRGB = RGB.test(hex);

  if (boolRGBA || boolRGB) {
    return hex;
  } else {
    let a = 1;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (hex.slice(7, 9) != '') {
      a = parseInt(hex.slice(7, 9), 16) / 255;
    }

    if (a > 0.99) {
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      let alpha = a.toFixed(2);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
}

export function queryToString(dimensionName, q) {
  // categorical variable
  if ('value' in q[0]) {
    if (q.length == 1) {
      return dimensionName + ' = ' + q[0].value;
      // continuous variable / range
    } else {
      return (
        dimensionName +
        ' IN (' +
        q.map(qr => "'" + qr.value + "'").join(', ') +
        ')'
      );
    }
  } else if ('minValue' in q[0]) {
    return dimensionName + ' ' + q[0].minValue + ' - ' + q[0].maxValue;
  } else {
    return '?';
  }
}

export function missingDomainFallback(e) {
  e.target.src = '/images/Blank-icon-128.png';
}

/**
 * localStorage wrappers that tolerate the store being unavailable or full.
 *
 * These hold UI preferences, which are never worth breaking a view over, so a
 * failure to read is reported as "nothing saved" and a failure to write is
 * dropped. Private browsing and quota exhaustion both surface as exceptions.
 */
export function readStoredJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? null : JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export function writeStoredJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    return;
  }
}
